"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "../../utils/utilsShared";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

interface TooltipContentProps extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  color?: string;
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, color, ...props }, ref) => {
  const getBackgroundColor = () => {
    if (!color) return "bg-vBlack";
    
    // Handle common color names and hex values
    const colorMap: Record<string, string> = {
      red: "bg-red-500",
      green: "bg-green-500", 
      blue: "bg-blue-500",
      yellow: "bg-yellow-500",
      purple: "bg-purple-500",
      pink: "bg-pink-500",
      indigo: "bg-indigo-500",
      gray: "bg-gray-500",
      black: "bg-black",
      white: "bg-white",
      customBlack: "bg-vBlack"
    };
    
    // Check if it's a predefined color name
    if (colorMap[color]) {
      return colorMap[color];
    }
    
    // If it's a hex color, return it directly
    if (color.startsWith('#')) {
      return `bg-[${color}]`;
    }
    
    // If it's a Tailwind class, return it directly
    if (color.startsWith('bg-')) {
      return color;
    }
    
    // Default fallback
    return "bg-customBlack";
  };

  return (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-lg border px-4 py-2 text-[12px] shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        getBackgroundColor(),
        className, // Make sure this doesn't override text color
        "text-white !important text-opacity-100" // Force text color
      )}
      {...props}
    />
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
